import { UI_ONLY_NODE_TYPES } from "../constants";
import { EDGE_MARKER_END } from "../hooks/useRuleLifecycle";
import {
	AFTER_LAST_SENTINEL_ID,
	CONTINUE_SENTINEL_ID,
	FOREACH_SENTINEL_ID,
	isSentinelId,
	LOOP_CONTINUE_TO_START_EDGE_ID,
	LOOP_START_TO_CONTINUE_EDGE_ID,
	LOOP_START_TO_END_EDGE_ID,
} from "../nodes/loop/loopTypes";
import { reconnectableForEdgeType, type RuleEdge, type RuleEdgeType } from "../types/edges";
import type { LoopNodeData, RuleNode } from "../types/nodes";
import { Transition } from "../types/rule-lifecycle";

const EDGE_TYPE_TO_WHEN: Record<string, string> = {
	"default": "always",
	"decision-true": "true",
	"decision-false": "false",
	"loop-start": "forEach",
	"loop-end": "endLoop",
	"loop-for-each-return": "always",
	"loop-continue-to-start": "always",
};

const WHEN_TO_EDGE_META: Record<string, { type: RuleEdgeType; sourceHandle: string }> = {
	"always": { type: "default", sourceHandle: "out" },
	"true": { type: "decision-true", sourceHandle: "true" },
	"false": { type: "decision-false", sourceHandle: "false" },
	"forEach": { type: "loop-start", sourceHandle: "loop-start" },
	"endLoop": { type: "loop-end", sourceHandle: "loop-end" },
};

/** Main canvas: loop exit is a normal edge (`out` + default); still persist as `when: endLoop`. */
export function transitionWhenFromMainCanvasEdge(e: RuleEdge, nodes: RuleNode[]): string {
	const src = nodes.find((n) => n.id === e.source);
	if (src?.type === "loop" && (e.sourceHandle === "loop-end" || e.sourceHandle === "out")) {
		return "endLoop";
	}
	return EDGE_TYPE_TO_WHEN[e.type ?? "default"] ?? "always";
}

type ParentLoopCanvasContext = { parentLoopId: string; parentLoopEdges: RuleEdge[] };

/** Outgoing edge from a nested `loop` node on the enclosing canvas (main or parent sub-canvas). */
function findLoopBlockExitSuccessor(loopId: string, parentEdges: RuleEdge[]): string | null {
	const outgoing = parentEdges.filter((e) => e.source === loopId);
	if (outgoing.length === 0) return null;
	const byLoopEnd = outgoing.find((e) => e.sourceHandle === "loop-end" || e.type === "loop-end");
	if (byLoopEnd?.target != null) return String(byLoopEnd.target);
	const byOut = outgoing.find(
		(e) => e.sourceHandle === "out" || e.sourceHandle == null || e.sourceHandle === "",
	);
	if (byOut?.target != null) return String(byOut.target);
	const first = outgoing[0];
	return first?.target != null ? String(first.target) : null;
}

/** Decision branch handle/type — drives `true`/`false` when values. */
function decisionBranchWhen(edge: RuleEdge): "true" | "false" | null {
	if (edge.sourceHandle === "true" || edge.type === "decision-true") return "true";
	if (edge.sourceHandle === "false" || edge.type === "decision-false") return "false";
	return null;
}

/** Synthetic structural edges are rebuilt on load; never persisted. */
function isStructuralLoopEdge(edge: RuleEdge): boolean {
	return (
		edge.id === LOOP_START_TO_END_EDGE_ID ||
		edge.id === LOOP_START_TO_CONTINUE_EDGE_ID ||
		edge.id === LOOP_CONTINUE_TO_START_EDGE_ID
	);
}

/**
 * Converts one loop's inner edges to scoped save transitions (`data.loopEdges`).
 *
 * Sentinel targets are encoded so the UI can rebuild them on load:
 * - End sentinel  → `to: loop's main-canvas successor id` (or loop id when none), `when: endLoop`.
 *   The edge stays an inner edge (`loopNodeId` set) but points at the node that runs after the loop.
 * - Continue sentinel → `to: loop id`, `when: true|false` for decision branches else `forEach`.
 * - Start sentinel source into the body → `when: forEach`, `sourceHandle: out`.
 *
 * Genuine self-edges (`from === to` on a body node) are preserved as-is so a node can loop to
 * itself. Only the degenerate sentinel→sentinel hop (both collapse to the loop id) is dropped.
 * Nested loops are serialized separately under their own `data.loopEdges` — not flattened here.
 */
export const loopEdgesToSaveTransitions = (
	loopNodeId: string,
	loopEdges: RuleEdge[],
	loopNodes: RuleNode[],
	parentContext: ParentLoopCanvasContext | undefined,
): Transition[] => {
	const ghostIds = new Set(loopNodes.filter((n) => n.type === "ghost").map((n) => n.id));
	const result: Transition[] = [];

	for (const edge of loopEdges) {
		if (isStructuralLoopEdge(edge)) continue;
		if (ghostIds.has(edge.source) || ghostIds.has(edge.target)) continue;

		// Start/Continue sources collapse onto the loop id (the loop header is the from-node).
		const from =
			edge.source === FOREACH_SENTINEL_ID || edge.source === CONTINUE_SENTINEL_ID
				? loopNodeId
				: edge.source;

		const branch = decisionBranchWhen(edge);
		const sourceHandle =
			typeof edge.sourceHandle === "string" && edge.sourceHandle !== "" ? edge.sourceHandle : undefined;

		let to: string;
		let when: string;
		let savedSourceHandle = sourceHandle;

		if (edge.target === AFTER_LAST_SENTINEL_ID) {
			// Loop End: persist the real successor so the backend chains past the loop;
			// still an inner edge (loopNodeId kept). UI retargets back to the End sentinel.
			const successor = parentContext
				? findLoopBlockExitSuccessor(loopNodeId, parentContext.parentLoopEdges)
				: null;
			to = successor ?? loopNodeId;
			when = "endLoop";
		} else if (edge.target === CONTINUE_SENTINEL_ID || edge.target === FOREACH_SENTINEL_ID) {
			// Loop Continue (legacy Start `in`): next iteration. Decision branches keep their when.
			to = loopNodeId;
			when = branch ?? "forEach";
		} else if (edge.source === FOREACH_SENTINEL_ID) {
			// Start sentinel → first body hop: backend needs when:forEach + out.
			to = edge.target;
			when = "forEach";
			savedSourceHandle = "out";
		} else {
			to = edge.target;
			when = EDGE_TYPE_TO_WHEN[edge.type ?? "default"] ?? "always";
		}

		// Sentinel→sentinel collapses to loopId→loopId; skip that degenerate hop only.
		const sentinelOnly = isSentinelId(edge.source) && isSentinelId(edge.target);
		if (sentinelOnly && from === loopNodeId && to === loopNodeId) continue;

		result.push({
			from,
			to,
			when,
			...(sentinelOnly ? {} : { loopNodeId }),
			...(savedSourceHandle ? { sourceHandle: savedSourceHandle } : {}),
		});
	}

	return ensureLoopHeaderForEachBodySeeds(loopNodeId, loopEdges, loopNodes, result);
};

/**
 * Derive the canonical forEach seed target from stored sub-canvas edges / loopNodes.
 */
function resolveForEachBodySeedTarget(loopNodeId: string, loopEdges: RuleEdge[], loopNodes: RuleNode[]): string {
	for (const e of loopEdges) {
		if (e.source !== FOREACH_SENTINEL_ID) continue;
		if (e.target === FOREACH_SENTINEL_ID || e.target === AFTER_LAST_SENTINEL_ID) continue;
		if (e.target === CONTINUE_SENTINEL_ID) return loopNodeId;
		return String(e.target);
	}
	const firstBody = (loopNodes ?? []).find((n) => n.type !== "ghost");
	if (firstBody) return firstBody.id;
	return loopNodeId;
}

/**
 * Ensure every loop header that has any non-endLoop outgoing inner transition also has at least
 * one `when:forEach` hop in `loopEdges`. Empty loops add `forEach` L→L.
 */
function ensureLoopHeaderForEachBodySeeds(
	loopId: string,
	loopEdges: RuleEdge[],
	loopNodes: RuleNode[],
	transitions: Transition[],
): Transition[] {
	const out = transitions.map((t) => ({ ...t }));
	const fromL = out.filter((t) => t.from === loopId);
	const whenNorm = (w: string) => String(w ?? "").toLowerCase();
	const hasForEach = fromL.some((t) => whenNorm(t.when) === "foreach");
	const nonEndLoopOut = fromL.filter((t) => whenNorm(t.when) !== "endloop");
	if (hasForEach || nonEndLoopOut.length === 0) return out;

	const seedTo = resolveForEachBodySeedTarget(loopId, loopEdges, loopNodes);
	const idxAlwaysSameHop = out.findIndex(
		(t) => t.from === loopId && t.to === seedTo && whenNorm(t.when) === "always",
	);
	if (idxAlwaysSameHop !== -1) {
		out[idxAlwaysSameHop] = {
			...out[idxAlwaysSameHop],
			when: "forEach",
			sourceHandle: "out",
			loopNodeId: loopId,
		};
		return out;
	}
	if (out.some((t) => t.from === loopId && t.to === seedTo && whenNorm(t.when) === "foreach")) {
		return out;
	}
	out.push({
		from: loopId,
		to: seedTo,
		when: "forEach",
		sourceHandle: "out",
		loopNodeId: loopId,
	});
	return out;
}

/**
 * Outer-canvas edges only. Inner loop control flow lives under each loop's `data.loopEdges`.
 */
export const edgesToTransitions = (edges: RuleEdge[], nodes: RuleNode[]): Transition[] => {
	const uiOnlyIds = new Set(nodes.filter((n) => UI_ONLY_NODE_TYPES.has(n.type ?? "")).map((n) => n.id));

	return edges
		.filter((e) => !uiOnlyIds.has(e.source) && !uiOnlyIds.has(e.target))
		.map((e) => ({
			from: e.source,
			to: e.target,
			when: transitionWhenFromMainCanvasEdge(e, nodes),
		}));
};

export const transitionsToEdges = (transitions: Transition[]): RuleEdge[] =>
	transitions.map((t) => {
		const meta = WHEN_TO_EDGE_META[t.when] ?? WHEN_TO_EDGE_META["always"];
		const reconnectable = reconnectableForEdgeType(meta.type);
		return {
			id: `edge__${t.from}-${meta.sourceHandle}-${t.to}`,
			markerEnd: EDGE_MARKER_END,
			source: String(t.from),
			sourceHandle: meta.sourceHandle,
			target: String(t.to),
			targetHandle: "in",
			type: meta.type,
			...(reconnectable ? { reconnectable } : {}),
		} as RuleEdge;
	});
